<template>
  <div class="page-container">
    <v-container>
      <v-row>
        <v-col cols="12">
          <!-- Encabezado -->
          <h1 class="text-h4 mb-2">
            Ciberseguridad para desarrolladores: Guía práctica 2026
          </h1>
          <p class="text-subtitle-1 mb-4">
            Protege tu código, tus datos y los de tus clientes con estas buenas prácticas
            esenciales.
          </p>

          <v-divider class="my-6"></v-divider>

          <!-- Introducción -->
          <h2 class="text-h5 mt-6 mb-3">
            ¿Por qué los desarrolladores deben preocuparse por la seguridad?
          </h2>
          <p>
            En 2026, los ataques cibernéticos son más sofisticados y frecuentes. Como
            desarrollador, no basta con que tu código funcione: debe ser
            <strong>seguro por diseño</strong>. Una vulnerabilidad en tu aplicación puede
            exponer datos sensibles de miles de usuarios, generar pérdidas económicas y
            dañar la reputación de tu negocio.
          </p>
          <p>
            Según el <strong>Informe de Vulnerabilidades 2025</strong>, el 43% de las
            brechas de seguridad están relacionadas con errores en el código de
            aplicaciones web. La buena noticia es que la mayoría de estos errores se
            pueden prevenir con prácticas simples.
          </p>

          <!-- Top 5 vulnerabilidades -->
          <h2 class="text-h5 mt-6 mb-3">
            Las 5 vulnerabilidades más comunes en aplicaciones web
          </h2>
          <v-simple-table class="mt-4">
            <thead>
              <tr>
                <th>Vulnerabilidad</th>
                <th>Descripción</th>
                <th>Riesgo</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><strong>Inyección SQL</strong></td>
                <td>El atacante inserta código SQL malicioso en entradas del usuario</td>
                <td>Alto</td>
              </tr>
              <tr>
                <td><strong>XSS (Cross-Site Scripting)</strong></td>
                <td>Inyección de scripts maliciosos en páginas web</td>
                <td>Alto</td>
              </tr>
              <tr>
                <td><strong>CSRF (Cross-Site Request Forgery)</strong></td>
                <td>El atacante engaña al usuario para realizar acciones no deseadas</td>
                <td>Medio-Alto</td>
              </tr>
              <tr>
                <td><strong>Autenticación rota</strong></td>
                <td>Fallos en la gestión de sesiones y contraseñas</td>
                <td>Crítico</td>
              </tr>
              <tr>
                <td><strong>Exposición de datos sensibles</strong></td>
                <td>Información confidencial sin cifrar o mal protegida</td>
                <td>Crítico</td>
              </tr>
            </tbody>
          </v-simple-table>

          <!-- Buenas prácticas -->
          <h2 class="text-h5 mt-6 mb-3">Buenas prácticas de seguridad para tu código</h2>
          <v-list dense>
            <v-list-item>
              <v-list-item-icon>
                <v-icon color="success" small>mdi-check-circle</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <strong>Valida y sanitiza todas las entradas</strong> — Nunca confíes en
                los datos que vienen del usuario.
              </v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-icon>
                <v-icon color="success" small>mdi-check-circle</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <strong>Usa consultas parametrizadas</strong> — Evita inyecciones SQL
                utilizando ORMs o consultas preparadas.
              </v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-icon>
                <v-icon color="success" small>mdi-check-circle</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <strong>Implementa autenticación robusta</strong> — Usa MFA (autenticación
                multifactor) y almacena contraseñas con bcrypt.
              </v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-icon>
                <v-icon color="success" small>mdi-check-circle</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <strong>Mantén dependencias actualizadas</strong> — Revisa periódicamente
                vulnerabilidades conocidas en tus librerías.
              </v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-icon>
                <v-icon color="success" small>mdi-check-circle</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <strong>Usa HTTPS en todas tus aplicaciones</strong> — Siempre cifra la
                comunicación entre cliente y servidor.
              </v-list-item-content>
            </v-list-item>
          </v-list>

          <!-- Herramientas recomendadas -->
          <h2 class="text-h5 mt-6 mb-3">
            Herramientas para auditar y mejorar la seguridad
          </h2>
          <v-row>
            <v-col cols="12" md="4" v-for="(tool, index) in securityTools" :key="index">
              <v-card outlined class="pa-3" height="100%">
                <v-card-title class="text-h6 pa-0 mb-2">{{ tool.name }}</v-card-title>
                <v-card-text class="pa-0">{{ tool.description }}</v-card-text>
                <v-card-actions class="pa-0 mt-2">
                  <v-btn text small color="primary" :href="tool.url" target="_blank"
                    >Ver más</v-btn
                  >
                </v-card-actions>
              </v-card>
            </v-col>
          </v-row>

          <!-- Ciberseguro -->
          <h2 class="text-h5 mt-6 mb-3">
            ¿Sabías que existe un seguro para ciberataques?
          </h2>
          <p>
            El <strong>ciberseguro</strong> es una póliza que protege a tu empresa ante
            pérdidas económicas derivadas de ataques informáticos, como ransomware,
            filtración de datos o interrupción del negocio. Si manejas información
            sensible de clientes, tener un ciberseguro no es un lujo, es una necesidad.
          </p>
          <v-alert type="info" class="mt-4">
            <strong>¿Quieres conocer más?</strong> En nuestra sección de
            <router-link to="/recursos/seguros-tech">Seguros para devs</router-link>
            te explicamos cómo elegir el mejor ciberseguro para tu negocio tecnológico.
          </v-alert>

          <v-divider class="my-6"></v-divider>

          <!-- Conclusión -->
          <h2 class="text-h5 mt-6 mb-3">Conclusión</h2>
          <p>
            La ciberseguridad no es solo responsabilidad de un equipo de seguridad; es
            parte del trabajo de cada desarrollador. Incorporar estas prácticas desde el
            inicio del desarrollo te ahorrará costos, protegerá a tus usuarios y
            fortalecerá la confianza en tu marca.
          </p>
          <p>
            Si quieres profundizar en algún tema específico, déjanos un comentario o
            contáctanos. Estamos aquí para ayudarte a construir software más seguro.
          </p>

          <p class="text-caption text-center mt-6">
            *Este artículo es solo para fines informativos. Para implementar políticas de
            seguridad, consulta con un especialista certificado.
          </p>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
export default {
  name: "PaginaCiberseguridad",
  data() {
    return {
      securityTools: [
        {
          name: "OWASP ZAP",
          description:
            "Escáner de vulnerabilidades en aplicaciones web. Ideal para pruebas de penetración.",
          url: "https://www.zaproxy.org/",
        },
        {
          name: "Snyk",
          description:
            "Detecta vulnerabilidades en dependencias y contenedores. Se integra con GitHub.",
          url: "https://snyk.io/",
        },
        {
          name: "SonarQube",
          description: "Analiza calidad y seguridad del código continuamente.",
          url: "https://www.sonarqube.org/",
        },
        {
          name: "Nikto",
          description: "Escáner de servidores web que detecta configuraciones inseguras.",
          url: "https://github.com/sullo/nikto",
        },
        {
          name: "Wireshark",
          description:
            "Analizador de tráfico de red para detectar comunicaciones sospechosas.",
          url: "https://www.wireshark.org/",
        },
        {
          name: "Nmap",
          description: "Escáner de puertos y servicios, fundamental para reconocimiento.",
          url: "https://nmap.org/",
        },
      ],
    };
  },
  mounted() {
    window.scrollTo(0, 0);
  },
};
</script>

<style scoped>
.page-container {
  max-width: 1100px;
  margin: 0 auto;
  padding: 80px 20px 40px;
}
th {
  background-color: #f5f5f5;
  font-weight: bold;
  padding: 12px;
  border: 1px solid #e0e0e0;
}
td {
  padding: 12px;
  border: 1px solid #e0e0e0;
}
.v-list-item {
  padding-left: 0;
}
.v-card {
  height: 100%;
}
</style>
